import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon13 from "../../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import { useAuthFetch } from '../../hooks/useAuthFetch';

function generateUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback UUID v4 generator
  const hex = [...Array(256).keys()].map(i => (i).toString(16).padStart(2, '0'));
  const r = crypto && crypto.getRandomValues ? crypto.getRandomValues(new Uint8Array(16)) : new Array(16).fill(0).map(() => Math.floor(Math.random() * 256));
  r[6] = (r[6] & 0x0f) | 0x40; // version 4
  r[8] = (r[8] & 0x3f) | 0x80; // variant
  return (
    hex[r[0]] + hex[r[1]] + hex[r[2]] + hex[r[3]] + '-' +
    hex[r[4]] + hex[r[5]] + '-' +
    hex[r[6]] + hex[r[7]] + '-' +
    hex[r[8]] + hex[r[9]] + '-' +
    hex[r[10]] + hex[r[11]] + hex[r[12]] + hex[r[13]] + hex[r[14]] + hex[r[15]]
  );
}

export default function StreetAutocompleteInput({
  value,
  onChange,
  onSelectSuggestion,
  onPlaceDetails,
  placeholder = 'Street',
  disabled = false,
  country = 'US',
  inputClassName = '',
  containerClassName = ''
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionToken] = useState(() => generateUUID());
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const portalRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);
  const debounceRef = useRef(null);
  const { authFetch, isReady } = useAuthFetch();

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      const clickedInsideInput = dropdownRef.current && dropdownRef.current.contains(e.target);
      const clickedInsidePortal = portalRef.current && portalRef.current.contains(e.target);
      if (!clickedInsideInput && !clickedInsidePortal) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Compute and update dropdown position when opened, on scroll and resize
  useEffect(() => {
    const updateRect = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownRect({
        left: rect.left + window.scrollX,
        top: rect.bottom + window.scrollY,
        width: rect.width
      });
    };
    if (open) {
      updateRect();
      window.addEventListener('scroll', updateRect, true);
      window.addEventListener('resize', updateRect);
    }
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open]);

  const fetchSuggestions = async (text) => {
    if (!isReady) return;
    if (!text || text.trim().length === 0) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_BASEURL}/api/places/autocomplete?q=${encodeURIComponent(text)}&sessionToken=${encodeURIComponent(sessionToken)}&country=${encodeURIComponent(country)}`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error('Failed to fetch suggestions');
      const data = await res.json();
      const preds = data?.data?.predictions || [];
      setSuggestions(preds);
      setOpen(true);
    } catch (err) {
      setSuggestions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    onChange && onChange(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(text);
    }, 400);
  };

  const handleSelect = async (s) => {
    const text = s?.description || s?.main_text || '';
    setQuery(text);
    onChange && onChange(text);
    onSelectSuggestion && onSelectSuggestion(s);

    // Optionally fetch place details if a handler is provided
    if (onPlaceDetails && s?.place_id && isReady) {
      try {
        const url = `${process.env.REACT_APP_BACKEND_BASEURL}/api/places/details?placeId=${encodeURIComponent(s.place_id)}&sessionToken=${encodeURIComponent(sessionToken)}`;
        const res = await authFetch(url);
        if (res.ok) {
          const data = await res.json();
          const details = data?.data || {};
          onPlaceDetails(details);
        }
      } catch (_) {
        // swallow errors, autocomplete still works
      }
    }
    setOpen(false);
  };

  const inputClasses = `w-full p-2 border rounded pr-8 ${inputClassName}`.trim();

  return (
    <div className={`relative ${containerClassName}`} ref={(el) => { dropdownRef.current = el; containerRef.current = el; }}>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={inputClasses}
        disabled={disabled}
        autoComplete="new-password"
        autoCorrect="off"
        spellCheck="false"
        data-form-type="other"
      />
      {loading ? (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-700"></div>
        </div>
      ) : (
        <img src={Icon13} alt="Search Icon" className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none" />
      )}

      {open && suggestions.length > 0 && dropdownRect && ReactDOM.createPortal(
        (
          <div
            className="bg-white border rounded-md mt-1 shadow-lg max-h-64 overflow-y-auto"
            style={{
              position: 'absolute',
              left: dropdownRect.left,
              top: dropdownRect.top,
              width: dropdownRect.width,
              zIndex: 9999
            }}
          >
            <ul>
              {suggestions.map((s, idx) => (
                <li
                  key={s.place_id || idx}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
                >
                  <div className="text-sm text-gray-900">{s.main_text || s.description}</div>
                  {s.secondary_text && (
                    <div className="text-xs text-gray-500">{s.secondary_text}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ),
        (() => {
          // Create a wrapper so we can hold a ref to the portal content
          const node = document.createElement('div');
          if (!portalRef.current) {
            portalRef.current = node;
            document.body.appendChild(node);
          }
          return portalRef.current;
        })()
      )}
    </div>
  );
}


